import {
	AnalyticsChatMessage,
	AnalyticsChatSummary,
	AnalyticsTurnPhase,
	AnalyticsTurnResult,
	AnalyticsTurnState,
} from "../../../../../services/analytics.service";

/**
 * Waiting for a slow turn without losing it (SIMS-7875).
 *
 * ## The situation this handles
 *
 * A chat turn is one POST, and that POST does not always come back. API
 * Gateway's REST integration stops waiting after 29 seconds while the server
 * keeps working and writes the answer to the conversation when it finishes. So
 * a failed request does not mean a failed turn, and treating it as one is what
 * produced the old behaviour: an error bubble for a report that was in fact
 * generated, and a retry that generated it a second time.
 *
 * The rule this file encodes: **when the request fails, ask the server what
 * happened to the turn instead of guessing from the error.** The server's own
 * state is the answer — it knows whether the question was accepted, whether the
 * work is still running, and what it produced.
 *
 * Two things make that possible:
 *
 * - a `turnId` we generate before sending, so the turn is identifiable even
 *   though the response that would have named it never arrived;
 * - the session id, which for an existing conversation we already have, and for
 *   a brand-new one we recover from the chat list by matching `currentTurnId`.
 *
 * Extracted from the component rather than written inline because jest excludes
 * `index.tsx` from coverage — the same reason `projection.ts` exists — and
 * because these are the decisions worth testing.
 */

/**
 * How often to poll while a turn is running.
 *
 * Two seconds, against an endpoint that returns one turn rather than the whole
 * transcript. Matches the "poll only while busy" pattern the Smart Program chat
 * already uses, at half its rate, because an analytics turn is measured in tens
 * of seconds rather than in seconds.
 */
export const TURN_POLL_INTERVAL_MS = 2_000;

/**
 * When to stop polling and say so.
 *
 * The server bounds a turn by its own invocation deadline and resolves an
 * abandoned one to FAILED, so this is a backstop for the case where polling
 * itself is what is broken. Generous, because the whole point of the story is
 * that a long report is a normal case and not a failure.
 */
export const TURN_POLL_TIMEOUT_MS = 15 * 60 * 1_000;

/** What the user reads while each phase runs. Plain progress, never jargon. */
export const TURN_PHASE_LABEL: Record<AnalyticsTurnPhase, string> = {
	THINKING: "Working out what you need…",
	QUERYING: "Querying your data…",
	RENDERING: "Building your report…",
	SUMMARIZING: "Writing up the results…",
};

/** Shown before the first phase arrives. */
export const TURN_WAITING_LABEL = "Thinking…";

/**
 * Shown once the request itself has expired and we are following the turn by
 * polling. It says the work is continuing, because it is.
 */
export const TURN_STILL_WORKING_LABEL =
	"Still working — this one is taking longer than usual. It will appear here when it is ready.";

/** Shown against a question whose turn never produced an answer. */
export const TURN_UNANSWERED_NOTE =
	"This didn't finish, so there is no answer to it. Send it again to retry.";

/** Last resort when a turn failed without the server saying why. */
export const TURN_GENERIC_FAILURE =
	"Something went wrong contacting the assistant.";

/** A turn we are following because its own response did not arrive. */
export interface PendingTurn {
	chatSessionId: string;
	turnId: string;
	/** For the retry affordance, and so the question stays on screen. */
	message: string;
	startedAt: number;
}

/**
 * A fresh turn id.
 *
 * `crypto.randomUUID` where available, with a fallback for older browsers and
 * for jsdom without the WebCrypto shim. Uniqueness per turn is all that is
 * required of it — the value is compared for equality and never interpreted.
 */
export const newTurnId = (): string => {
	const cryptoRef =
		typeof globalThis === "undefined"
			? undefined
			: (globalThis.crypto as Crypto | undefined);
	if (cryptoRef && typeof cryptoRef.randomUUID === "function") {
		return cryptoRef.randomUUID();
	}
	return `t-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

/** Is this turn still being worked on? */
export const isTurnPending = (
	turn: Pick<AnalyticsTurnResult, "status"> | undefined,
): boolean => turn?.status === "IN_PROGRESS";

/** Has this turn reached an outcome — an answer or a failure? */
export const isTurnSettled = (
	turn: Pick<AnalyticsTurnResult, "status"> | undefined,
): boolean =>
	turn?.status === "READY" ||
	turn?.status === "AWAITING_INPUT" ||
	turn?.status === "FAILED";

/**
 * React Query's `refetchInterval`: poll while the turn runs, stop the moment it
 * settles, and give up after the backstop.
 */
export const turnPollInterval = (
	turn: Pick<AnalyticsTurnResult, "status"> | undefined,
	startedAt: number,
	now: number = Date.now(),
): number | false => {
	if (isTurnSettled(turn)) return false;
	if (now - startedAt > TURN_POLL_TIMEOUT_MS) return false;
	return TURN_POLL_INTERVAL_MS;
};

/** The progress line for a turn being followed. */
export const turnProgressLabel = (
	turn: Pick<AnalyticsTurnResult, "phase"> | undefined,
	handedOff: boolean,
): string => {
	if (turn?.phase) return TURN_PHASE_LABEL[turn.phase];
	return handedOff ? TURN_STILL_WORKING_LABEL : TURN_WAITING_LABEL;
};

/**
 * Find the session a turn belongs to, when the response that would have named
 * it never arrived.
 *
 * This is what makes the very first turn of a new conversation recoverable: the
 * session id is allocated server-side, so without this the client would hold a
 * turn id and nothing to poll.
 */
export const findSessionForTurn = (
	chats: AnalyticsChatSummary[] | undefined,
	turnId: string,
): AnalyticsChatSummary | undefined =>
	(chats ?? []).find((chat) => chat.currentTurnId === turnId);

/**
 * What to show for a settled turn.
 *
 * A FAILED turn is rendered as the server's own explanation rather than as an
 * error, because by this point the server has already phrased it for a reader —
 * and a turn that ran out of room is not the reader's mistake.
 */
export const settledTurnBubble = (
	turn: AnalyticsTurnResult,
): { content: string; report?: AnalyticsTurnResult["report"] } => ({
	content: turn.message || TURN_GENERIC_FAILURE,
	...(turn.report ? { report: turn.report } : {}),
});

/**
 * Should this transcript message be marked as never answered?
 *
 * True only for the LAST message when it is the user's and the conversation is
 * not waiting on the user for anything — which is exactly the shape the
 * durability fix leaves behind when a turn is killed: the question is safely on
 * record with nothing after it.
 *
 * Answers the acceptance criterion directly: reloading the conversation shows
 * the question as unanswered rather than missing.
 */
export const isUnansweredQuestion = (
	messages: Pick<AnalyticsChatMessage, "role">[],
	index: number,
	sessionStatus: AnalyticsTurnState | string | undefined,
): boolean =>
	index === messages.length - 1 &&
	messages[index]?.role === "USER" &&
	sessionStatus !== "IN_PROGRESS";

/**
 * The turn still to be followed after loading a conversation from history.
 *
 * A conversation whose last turn is still running is picked up where it was
 * left, so switching away and back does not abandon a report that is being
 * built. Nothing is returned for a settled conversation.
 */
export const resumableTurn = (
	session: {
		_id: string;
		status?: string;
		currentTurnId?: string;
		messages?: Pick<AnalyticsChatMessage, "role" | "content">[];
	},
	now: number = Date.now(),
): PendingTurn | undefined => {
	if (session.status !== "IN_PROGRESS" || !session.currentTurnId) {
		return undefined;
	}
	const messages = session.messages ?? [];
	const lastUser = [...messages].reverse().find((m) => m.role === "USER");
	return {
		chatSessionId: session._id,
		turnId: session.currentTurnId,
		message: lastUser?.content ?? "",
		startedAt: now,
	};
};
